import { Box } from '@mui/material';
import PopulatedFundOpportunityFieldsPreview from 'PopulatedFundOpportunityFieldsPreview';
import React from 'react';
import GenericChatWindow from '../GenericChatWindow';
import StyledSection from '../styles/StyledSection';
import { StyledHeading } from '../styles/styles';
import FundOpportunityDocumentUploadPanel from './aiChatFundOpportunityCreator/FundOpportunityDocumentUploadPanel';
import { AIChatFundOpportunityCreatorProps } from './aiChatFundOpportunityCreator/types';
import { useAIChatFundOpportunityCreator } from './aiChatFundOpportunityCreator/useAIChatFundOpportunityCreator';

// Matches the page's fixed chrome above and below this card — the header
// bar + its top gap (90px, see CONTENT_TOP_PADDING_EXTRA) and
// MainLayoutBase's own bottom padding (3.75rem/60px) — so the chat fills
// the rest of the viewport instead of sitting in a short, cropped box.
// Re-measure if either of those changes.
const PAGE_CHROME_HEIGHT = '150px';

function CreatorChat({ creator }: { creator: ReturnType<typeof useAIChatFundOpportunityCreator> }) {
  return (
    <>
      <FundOpportunityDocumentUploadPanel
        uploadProgress={creator.uploadProgress}
        uploadedDocuments={creator.uploadedDocuments}
        isProcessing={creator.isProcessing}
        onRemoveDocument={creator.handleRemoveDocument}
        onProcessDocuments={creator.handleProcessDocuments}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <GenericChatWindow
          placeholderPrompt="Enter Response or Ask a Question"
          messages={creator.messages}
          isProcessing={creator.isProcessing}
          onSubmit={(input: string) => creator.handleSendMessage(input)}
          onDocumentUpload={creator.handleDocumentUpload}
          isUploadingFile={creator.isUploadingFile}
          messageAreaHeight={{ min: '0px', max: 'none' }}
          unifiedComposer
        />
      </Box>
    </>
  );
}

const AIChatFundOpportunityCreator: React.FC<AIChatFundOpportunityCreatorProps> = ({
  onSuccess,
}) => {
  const creator = useAIChatFundOpportunityCreator({ onSuccess });
  // Show the fields column as soon as the chat has returned data — even when
  // every field is still empty, so required ones can be filled in directly.
  const hasFields = Boolean(creator.currentData);

  return (
    <StyledSection
      tone="tone1"
      noTopPadding
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // border-box so the section's own padding/border count toward the
        // explicit height instead of inflating past it.
        boxSizing: 'border-box',
        height: `calc(100vh - ${PAGE_CHROME_HEIGHT})`,
        minHeight: '480px',
      }}
    >
      <StyledHeading tone="tone1" fullTopSpacing>
        New Fund Opportunity
      </StyledHeading>
      <StyledHeading headerVariant="subheading" tone="tone1" sx={{ mb: 2 }}>
        Type fund details directly into the chat or upload a document to pull out key data points to
        get started.
      </StyledHeading>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: hasFields ? '1 1 58%' : '1 1 100%',
            minHeight: 0,
          }}
        >
          <CreatorChat creator={creator} />
        </Box>

        {hasFields && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 42%', minHeight: 0 }}>
            <PopulatedFundOpportunityFieldsPreview
              data={creator.currentData}
              missingRequiredFields={creator.missingRequiredFields}
              isCreating={creator.isCreating}
              onComplete={() => creator.handleComplete()}
              onRemoveField={creator.handleRemoveField}
              onEditField={creator.handleEditField}
              onRemoveAddress={creator.handleRemoveAddress}
              onRemoveAddresses={creator.handleRemoveAddresses}
              onSetField={creator.handleSetField}
            />
          </Box>
        )}
      </Box>
    </StyledSection>
  );
};

export default AIChatFundOpportunityCreator;
